/**
 * Autopilot — Learn-Mode inline tips.
 *
 * Anchored across `routes/social/Autopilot.tsx` and the
 * `components/autopilot/*` pieces it renders.
 */

import type { LearnTip } from "./types";

export const autopilotTips: LearnTip[] = [
  {
    key: "social-autopilot",
    title: "Autopilot",
    body: "Plans up to a month of on-brand draft posts for a client in one go. Every draft lands in Approvals or Drafts for review — nothing publishes automatically.",
  },
  {
    key: "social-autopilot-setup",
    title: "Plan Settings",
    body: "Choose the client, date range, and which platforms to plan for. These settings drive how many drafts Autopilot generates and what content mix it aims for.",
  },
  {
    key: "social-autopilot-mix",
    title: "Content Mix",
    body: "Sets what share of the plan goes to each content pillar (e.g. educational, promotional, behind-the-scenes). Adjust the sliders, or reset to the recommended mix for this client.",
  },
  {
    key: "social-autopilot-readiness",
    title: "Brand Readiness",
    body: "A quick score of how complete this client's brand kit, voice guide, and knowledge base are. The more complete they are, the more on-brand Autopilot's drafts will be — any gaps are listed below the score.",
  },
  {
    key: "social-autopilot-generate",
    title: "Generating Drafts",
    body: "Starts Autopilot writing and scheduling draft posts for the plan above. Optionally route every draft straight to Approvals for review instead of leaving it as an unsubmitted draft.",
  },
  {
    key: "social-autopilot-plan",
    title: "Your Plan",
    body: "The slate of post slots Autopilot built for this client, laid out week by week. Nothing here is scheduled or published yet — drafts go to review first.",
  },
  {
    key: "social-autopilot-results",
    title: "Generated Drafts",
    body: "The drafts Autopilot produced from your plan. Review them in Drafts or Approvals, or jump to the Calendar to see them laid out by date.",
  },
  {
    key: "social-autopilot-attach-all",
    title: "Attach All Images",
    body: "Renders each draft's design and attaches the finished pages as that post's images in one action — useful since Instagram can't publish a post with no image attached.",
  },
  {
    key: "social-autopilot-video-mode",
    title: "Static vs Video",
    body: "Switches Autopilot between static image drafts and video drafts. Video Autopilot only plans slots for now — a later step adds the rendering, review and selection of the actual clips.",
  },
  {
    key: "social-autopilot-video-readiness",
    title: "Video Readiness",
    body: "Whether this client has eligible footage, a connected video-capable account, and the captioning service is reachable. Footage with no consent, or footage flagged for a HIPAA-covered client, never counts as eligible.",
  },
  {
    key: "social-autopilot-video-policy",
    title: "Video Policy",
    body: "Per-client guardrails: how many reels per week, which networks and recipes are allowed, and whether a client also reviews drafts before they go out. Enabling this never publishes anything — drafts always need approval.",
  },
  {
    key: "social-autopilot-video-plan",
    title: "Video Plan",
    body: "The video slots Autopilot planned for this client, each with its recipe, hook, and footage status. Slots marked 'Awaiting footage' need footage added in the Footage Library before they can be rendered.",
  },
  {
    key: "social-autopilot-video-candidates",
    title: "Candidates",
    body: "Generate renders a few different cuts for this slot, QA's each one, and drafts the best-scoring candidate automatically. You can compare any two side by side, pick a different one yourself, or regenerate with feedback if none of them feel right.",
  },
  {
    key: "social-autopilot-video-trial-reels",
    title: "Instagram Trial Reels",
    body: "Trial reels are shown to people who don't follow the account first, so you can see how a reel performs before your followers see it.",
    bullets: ["Only shown when Instagram is one of the selected platforms."],
  },
];
